/**
 * DebugPanelHeader — always-visible bar across the bottom of the viewport.
 * 32px tall. Contains chevron toggle, title, status indicator, and primary
 * actions (Copy, Export, Auto-refresh, Section search).
 */
import { ChevronDown, ChevronUp, Clipboard, Download, RefreshCw, Search, Bug } from 'lucide-react';
import { type ChangeEvent } from 'react';

export interface DebugPanelHeaderProps {
  expanded: boolean;
  onToggleExpanded: () => void;
  errorCount: number;
  apiCount: number;
  searchTerm: string;
  onSearchChange: (v: string) => void;
  autoRefresh: boolean;
  onToggleAutoRefresh: () => void;
  onCopy: () => void;
  onExport: () => void;
  /** Inline confirmation that fades out (Copy / Export feedback). */
  flash: string | null;
}

export function DebugPanelHeader(props: DebugPanelHeaderProps): JSX.Element {
  const {
    expanded, onToggleExpanded, errorCount, apiCount,
    searchTerm, onSearchChange, autoRefresh, onToggleAutoRefresh,
    onCopy, onExport, flash,
  } = props;

  return (
    <div className="pcb-debug-header" role="region" aria-label="Debug Panel">
      <button
        type="button"
        className="pcb-debug-header__toggle"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        title={expanded ? 'Collapse Debug Panel' : 'Expand Debug Panel'}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        <Bug size={13} className="pcb-debug-header__icon" />
        <span className="pcb-debug-header__title">Debug</span>
        {errorCount > 0 && (
          <span
            className="pcb-debug-header__indicator is-err"
            title={`${errorCount} error/warning${errorCount === 1 ? '' : 's'} captured`}
          >
            {errorCount}
          </span>
        )}
        {errorCount === 0 && apiCount > 0 && (
          <span className="pcb-debug-header__indicator is-ok" title="No errors captured">
            ●
          </span>
        )}
        <span className="pcb-debug-header__sub">
          {apiCount} API call{apiCount === 1 ? '' : 's'}
        </span>
      </button>

      <div className="pcb-debug-header__search">
        <Search size={12} className="pcb-debug-header__search-icon" />
        <input
          type="search"
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          placeholder="Filter sections…"
          className="pcb-debug-header__search-input"
          aria-label="Filter debug sections"
        />
      </div>

      <div className="pcb-debug-header__actions">
        <button
          type="button"
          className={`pcb-debug-header__action ${autoRefresh ? 'is-on' : ''}`}
          onClick={onToggleAutoRefresh}
          title={autoRefresh ? 'Auto-refresh ON (every 2s)' : 'Toggle auto-refresh'}
          aria-pressed={autoRefresh}
        >
          <RefreshCw size={13} />
        </button>
        <button
          type="button"
          className="pcb-debug-header__action"
          onClick={onCopy}
          title="Copy full debug payload (redacted) to clipboard"
        >
          <Clipboard size={13} />
        </button>
        <button
          type="button"
          className="pcb-debug-header__action"
          onClick={onExport}
          title="Export full debug payload (redacted) as JSON file"
        >
          <Download size={13} />
        </button>
        {flash && <span className="pcb-debug-header__flash" role="status">{flash}</span>}
      </div>
    </div>
  );
}
